import { useState } from 'react';
import { parseEpisodeTitle } from '../../utils/time';
import { HiPlay, HiPlus, HiCheck } from 'react-icons/hi2';
import { DEFAULT_STATION_IMAGE } from '../../constants';
import type { SearchResult } from '../../types';

interface EpisodeCardProps {
  episode: SearchResult;
  fallbackImage: string;
  tabIndex?: number;
  isInQueue?: boolean;
  onAddToQueue?: () => void;
  onNavigate?: (episodeId: number) => void;
}

export function EpisodeCard({
  episode,
  fallbackImage,
  tabIndex,
  isInQueue = false,
  onAddToQueue,
  onNavigate,
}: EpisodeCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { name: episodeName } = parseEpisodeTitle(episode.episodetitle);
  const formattedTime = episode.time_string_format || '';

  const handleClick = () => {
    onNavigate?.(episode.episodeid);
  };

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-[var(--radius-xl)]
                 bg-[var(--color-surface)]/60 backdrop-blur-sm
                 border border-[var(--color-border)]/50
                 hover:bg-[var(--color-surface)]/80
                 hover:border-[var(--color-border)]
                 transition-all duration-150 ease-out
                 group"
    >
      {/* Thumbnail with play overlay - links to episode */}
      <button
        type="button"
        onClick={handleClick}
        aria-label={episode.episodetitle}
        tabIndex={tabIndex}
        className="relative w-14 h-14 flex-shrink-0 rounded-[var(--radius-lg)]
                   overflow-hidden shadow-md bg-[var(--color-border)]
                   focus:outline-none focus-visible:ring-2
                   focus-visible:ring-[var(--color-primary)]
                   active:scale-95 transition-transform"
      >
        <img
          src={episode.image}
          alt={episodeName}
          loading="lazy"
          className={`w-full h-full object-cover transition-opacity duration-300
                     ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== fallbackImage && fallbackImage) {
              target.src = fallbackImage;
            } else if (!target.src.endsWith(DEFAULT_STATION_IMAGE)) {
              target.src = DEFAULT_STATION_IMAGE;
            }
            setImageLoaded(true);
          }}
        />
        {/* Play overlay on hover */}
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center
                        opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          <HiPlay size={24} className="text-white" />
        </div>
      </button>

      {/* Episode info - also links to episode */}
      <button
        type="button"
        onClick={handleClick}
        className="flex-1 min-w-0 text-left focus:outline-none"
      >
        <h2 className="text-sm font-medium text-[var(--color-text)] truncate
                       leading-tight">
          {episodeName}
        </h2>
        {formattedTime && (
          <span className="text-xs text-[var(--color-text-muted)] mt-1 block">
            {formattedTime}
          </span>
        )}
      </button>

      {/* Add to queue button */}
      {onAddToQueue && (
        isInQueue ? (
          <div className="flex-shrink-0 w-8 h-8 rounded-full
                          bg-[var(--color-primary)]/10
                          flex items-center justify-center">
            <HiCheck size={16} className="text-[var(--color-primary)]" />
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToQueue();
            }}
            aria-label="Add to queue"
            title="Add to queue"
            className="flex-shrink-0 w-8 h-8 rounded-full
                       bg-[var(--color-surface-elevated)]/80
                       flex items-center justify-center
                       opacity-0 group-hover:opacity-100
                       hover:bg-[var(--color-primary)]/20
                       active:scale-95
                       transition-all duration-150
                       focus:outline-none focus-visible:ring-2
                       focus-visible:ring-[var(--color-primary)]"
          >
            <HiPlus size={16} className="text-[var(--color-primary)]" />
          </button>
        )
      )}

      {/* Play indicator when no queue button */}
      {!onAddToQueue && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full
                        bg-[var(--color-primary)]/10
                        flex items-center justify-center
                        opacity-0 group-hover:opacity-100
                        transition-opacity duration-150">
          <HiPlay size={14} className="text-[var(--color-primary)] ml-0.5" />
        </div>
      )}
    </div>
  );
}
